import { create } from "zustand";
import type { SimulationPrecisionMode } from "../types/astronomy";
import {
  advanceLiveSimulationDate,
  getLiveSimulationDate,
  setLiveSimulationDate,
} from "./liveClock";

export const TIME_SCALE_OPTIONS = [
  { label: "1 s/s", value: 1 },
  { label: "1 min/s", value: 60 },
  { label: "1 h/s", value: 3600 },
  { label: "1 dia/s", value: 86_400 },
  { label: "7 dias/s", value: 604_800 },
  { label: "30 dias/s", value: 2_592_000 },
  { label: "365 dias/s", value: 31_536_000 },
] as const;

interface SimulationStore {
  currentDateUtc: Date;
  isPlaying: boolean;
  timeScale: number;
  direction: 1 | -1;
  precisionMode: SimulationPrecisionMode;
  setDate: (date: Date) => void;
  setPlaying: (value: boolean) => void;
  setTimeScale: (value: number) => void;
  setDirection: (value: 1 | -1) => void;
  setPrecisionMode: (mode: SimulationPrecisionMode) => void;
  resetToNow: () => void;
  /** Avança o relógio vivo; sincroniza o estado React só quando syncUi=true. */
  advance: (realDeltaSeconds: number, syncUi?: boolean) => void;
  syncUiDate: () => void;
}

export const useSimulationStore = create<SimulationStore>((set, get) => ({
  currentDateUtc: new Date(),
  isPlaying: false,
  timeScale: 86_400,
  direction: 1,
  precisionMode: "kepler",
  setDate: (date) => {
    const next = new Date(date.getTime());
    setLiveSimulationDate(next);
    set({ currentDateUtc: next });
  },
  setPlaying: (value) => set({ isPlaying: value }),
  setTimeScale: (value) => set({ timeScale: value }),
  setDirection: (value) => set({ direction: value }),
  setPrecisionMode: (mode) => set({ precisionMode: mode }),
  resetToNow: () => {
    const now = new Date();
    setLiveSimulationDate(now);
    set({ currentDateUtc: now, isPlaying: false });
  },
  advance: (realDeltaSeconds, syncUi = false) => {
    const { isPlaying, timeScale, direction } = get();
    if (!isPlaying) return;
    const simulatedDeltaMs = realDeltaSeconds * timeScale * 1000 * direction;
    const next = advanceLiveSimulationDate(simulatedDeltaMs);
    if (syncUi) set({ currentDateUtc: new Date(next.getTime()) });
  },
  syncUiDate: () => {
    set({ currentDateUtc: new Date(getLiveSimulationDate().getTime()) });
  },
}));
